import { Link } from "react-router-dom";
import { X, MapPin, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { resolveImg } from "@/lib/api";
import { locationLabel } from "@/lib/content";

function FeedCard({ to, image, kicker, title, text, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group flex flex-col h-full border border-ptf-primary/12 bg-white hover:border-ptf-primary/30 transition-colors overflow-hidden">
      {image && (
        <div className="aspect-[16/9] overflow-hidden">
          <img src={resolveImg(image)} alt={title} className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" loading="lazy" />
        </div>
      )}
      <div className="p-6 flex flex-col flex-1">
        <span className="text-[0.6rem] uppercase tracking-[0.16em] text-ptf-secondary">{kicker}</span>
        <h4 className="font-display uppercase text-xl tracking-tight text-ptf-primary mt-2 leading-none group-hover:text-ptf-secondary transition-colors">{title}</h4>
        {text && <p className="mt-3 text-sm text-ptf-text/70 leading-relaxed line-clamp-2 flex-1">{text}</p>}
        <span className="mt-4 inline-flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-ptf-primary">
          Read more <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}

export default function RegionalFeed({ title, programmes = [], stories = [], experiences = [], onClear }) {
  return (
    <Reveal>
      <div data-testid="regional-feed" className="border border-ptf-primary/15 bg-ptf-primary/[0.03] p-7 md:p-10">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <p className="text-[0.65rem] uppercase tracking-[0.24em] text-ptf-secondary mb-2 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" /> Regional feed
            </p>
            <h3 data-testid="regional-feed-title" className="font-display font-extrabold uppercase text-3xl md:text-4xl tracking-tight text-ptf-primary leading-none">
              Work in {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClear}
            data-testid="clear-region"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-ptf-secondary hover:text-ptf-primary transition-colors"
          >
            <X className="h-4 w-4" /> Clear
          </button>
        </div>

        {programmes.length === 0 && stories.length === 0 && experiences.length === 0 ? (
          <p className="text-sm italic font-serif-ed text-ptf-text/50">Nothing documented here yet.</p>
        ) : (
          <div className="space-y-10">
            {programmes.length > 0 && (
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.22em] text-ptf-primary/50 mb-4">
                  Programmes ({programmes.length})
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {programmes.map((p) => (
                    <FeedCard
                      key={p.id}
                      to={`/programmes/${p.slug || p.id}`}
                      testid={`feed-programme-${p.id}`}
                      image={p.images?.[0]}
                      kicker={`${p.status}${locationLabel(p) ? " · " + locationLabel(p) : ""}`}
                      title={p.name}
                      text={p.objective}
                    />
                  ))}
                </div>
              </div>
            )}
            {experiences.length > 0 && (
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.22em] text-ptf-primary/50 mb-4">
                  Field experiences ({experiences.length})
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {experiences.map((j) => (
                    <FeedCard
                      key={j.id}
                      to={`/exploration/${j.id}`}
                      testid={`feed-experience-${j.id}`}
                      image={j.images?.[0]}
                      kicker={`Field Experience${j.duration_days > 0 ? ` · ${j.duration_days} days` : ""}`}
                      title={j.title}
                      text={j.summary}
                    />
                  ))}
                </div>
              </div>
            )}
            {stories.length > 0 && (
              <div>
                <p className="text-[0.65rem] uppercase tracking-[0.22em] text-ptf-primary/50 mb-4">
                  Stories ({stories.length})
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {stories.map((s) => (
                    <FeedCard
                      key={s.id}
                      to={`/stories/${s.id}`}
                      testid={`feed-story-${s.id}`}
                      image={s.image}
                      kicker={`${s.category}${s.date ? " · " + s.date : ""}`}
                      title={s.title}
                      text={s.excerpt}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Reveal>
  );
}
